import React from 'react'
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { useCodeValues } from "@/hooks/use-code-values"
import { Timesheet } from '@/hooks/use-timesheet-reports'
import { cn } from "@/lib/utils"
import { useFormatters } from "@/hooks/use-formatters"
import { FileText } from "lucide-react"
import { ParentNavigationLink } from '../ui/parent-navigation-link'
import { SortIcon, getAriaSortValue } from '@/components/ui/sort-icon'

// Timesheet Status constants
const TIMESHEET_STATUS = {
  NEW: 1,
  DRAFT: 2,
  PENDING_CLIENT_REVIEW: 3,
  APPROVED: 4,
  DECLINED: 5,
  RECALLED: 7,
  APPROVED_AND_ACCEPTED: 10,
  UNSUBMITTED: 11,
  PENDING_BACKOFFICE_REVIEW: 12,
  PENDING_SYSTEM_APPROVAL: 13,
  HOLDING: 14,
} as const

interface TimesheetsContentProps {
  timesheets: Timesheet[]
  loading?: boolean
  error?: string | null
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (field: string) => void
}

// Helper function to get status badge className based on timesheet status ID
const getTimesheetStatusClassName = (statusId: number): string => {
  switch (statusId) {
    case TIMESHEET_STATUS.APPROVED:
    case TIMESHEET_STATUS.APPROVED_AND_ACCEPTED:
      return "bg-green-100 text-green-800 hover:bg-green-100"
    case TIMESHEET_STATUS.DECLINED:
      return "bg-red-100 text-red-800 hover:bg-red-100"
    case TIMESHEET_STATUS.NEW:
    case TIMESHEET_STATUS.DRAFT:
    case TIMESHEET_STATUS.UNSUBMITTED:
      return "bg-orange-100 text-orange-800 hover:bg-orange-100"
    case TIMESHEET_STATUS.PENDING_CLIENT_REVIEW:
    case TIMESHEET_STATUS.PENDING_BACKOFFICE_REVIEW:
    case TIMESHEET_STATUS.PENDING_SYSTEM_APPROVAL:
      return "bg-blue-100 text-blue-800 hover:bg-blue-100"
    case TIMESHEET_STATUS.RECALLED:
    case TIMESHEET_STATUS.HOLDING:
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100"
  }
}

export function TimesheetsContent({ timesheets, loading = false, error, sortField, sortDirection, onSort }: TimesheetsContentProps) {
  const { getCodeValueText } = useCodeValues()
  const { formatDecimal, formatDate } = useFormatters()

  const renderSortIcon = (field: string) => (
    <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-slate-600">
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span>Loading timesheets...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <p>Error loading timesheets: {error}</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table className="w-full">
            <TableHeader>
              <TableRow className="border-b border-slate-200 bg-slate-50/50">
                <TableHead
                  className="font-semibold text-slate-700"
                  aria-sort={getAriaSortValue('EntityId', sortField, sortDirection)}
                >
                  <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('EntityId')}>
                    ID
                    {renderSortIcon('EntityId')}
                  </button>
                </TableHead>
                <TableHead
                  className="font-semibold text-slate-700"
                  aria-sort={getAriaSortValue('WorkerName', sortField, sortDirection)}
                >
                  <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('WorkerName')}>
                    Worker Name
                    {renderSortIcon('WorkerName')}
                  </button>
                </TableHead>
                <TableHead
                  className="font-semibold text-slate-700"
                  aria-sort={getAriaSortValue('FromDate', sortField, sortDirection)}
                >
                  <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('FromDate')}>
                    From Date
                    {renderSortIcon('FromDate')}
                  </button>
                </TableHead>
                <TableHead
                  className="font-semibold text-slate-700"
                  aria-sort={getAriaSortValue('ToDate', sortField, sortDirection)}
                >
                  <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('ToDate')}>
                    To Date
                    {renderSortIcon('ToDate')}
                  </button>
                </TableHead>
                <TableHead
                  className="text-right font-semibold text-slate-700"
                  aria-sort={getAriaSortValue('EngagementBillableUnits', sortField, sortDirection)}
                >
                  <button type="button" className="flex items-center justify-end w-full hover:text-slate-900" onClick={() => onSort?.('EngagementBillableUnits')}>
                    Billable Units
                    {renderSortIcon('EngagementBillableUnits')}
                  </button>
                </TableHead>
                <TableHead
                  className="text-right font-semibold text-slate-700"
                  aria-sort={getAriaSortValue('EngagementNonBillableUnits', sortField, sortDirection)}
                >
                  <button type="button" className="flex items-center justify-end w-full hover:text-slate-900" onClick={() => onSort?.('EngagementNonBillableUnits')}>
                    Non-Billable Units
                    {renderSortIcon('EngagementNonBillableUnits')}
                  </button>
                </TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timesheets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={100} className="text-center py-12">
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                        <FileText className="w-8 h-8 text-slate-400" />
                      </div>
                      <div className="text-slate-500">No timesheets found</div>
                      <p className="text-sm text-slate-400">There are no timesheets available for this engagement</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                timesheets.map((timesheet, index) => {
                  return (
                    <TableRow
                      key={timesheet.EntityId}
                      className={cn(
                        "border-b border-slate-100 hover:bg-blue-50/50 transition-colors",
                        index % 2 === 0 ? "bg-white" : "bg-slate-50/30",
                      )}
                    >
                      <TableCell className="font-mono text-sm text-slate-600">
                        <ParentNavigationLink route={`/next/timesheet/${timesheet.EntityId}`}>
                          {timesheet.EntityId}
                        </ParentNavigationLink>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {timesheet.WorkerName}
                      </TableCell>
                      <TableCell className="text-slate-700 whitespace-nowrap">
                        {formatDate(timesheet.FromDate, '-')}
                      </TableCell>
                      <TableCell className="text-slate-700 whitespace-nowrap">
                        {formatDate(timesheet.ToDate, '-')}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">
                        {formatDecimal(timesheet.EngagementBillableUnits)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">
                        {formatDecimal(timesheet.EngagementNonBillableUnits)}
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("font-medium whitespace-nowrap", getTimesheetStatusClassName(timesheet.TimeSheetStatusId))}>
                          {getCodeValueText(timesheet.TimeSheetStatusId, 'timesheet.CodeTimeSheetStatus')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
  )
}